import React, { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'nudgesmart_whats_new_v1';

/**
 * Entry type badges configuration
 *
 * NOTE: `attention` is not a valid `<s-badge tone>` value (web components support
 * `info | success | warning | critical | neutral`). FIXED entries map to `warning`.
 */
const ENTRY_TYPES = {
  new: { label: 'NEW', tone: 'success' },
  improved: { label: 'IMPROVED', tone: 'info' },
  fixed: { label: 'FIXED', tone: 'warning' },
  deprecated: { label: 'DEPRECATED', tone: 'warning' },
};

/**
 * Format date string to relative or short format
 */
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * Single changelog entry row
 */
const EntryItem = ({ entry, isUnread }) => {
  const typeConfig = ENTRY_TYPES[entry.type] || ENTRY_TYPES.new;

  return (
    <s-box
      padding="small-200"
      background={isUnread ? 'subdued' : undefined}
      borderRadius="small"
    >
      <s-stack direction="block" gap="small-300">
        <s-stack direction="inline" gap="small-200" justifyContent="space-between" alignItems="center">
          <s-stack direction="inline" gap="small-200" alignItems="center">
            {/* @ts-expect-error dynamic tone from object lookup */}
            <s-badge tone={typeConfig.tone}>
              {typeConfig.label}
            </s-badge>
            <s-text type="strong">
              {entry.title}
            </s-text>
          </s-stack>
          <s-text color="subdued">
            {formatDate(entry.date)}
          </s-text>
        </s-stack>
        {entry.description && (
          <s-paragraph color="subdued">
            {entry.description}
          </s-paragraph>
        )}
      </s-stack>
    </s-box>
  );
};

/**
 * WhatsNew Component
 *
 * A changelog widget that displays latest updates from a static JSON file.
 * Features:
 * - Badge system: NEW (green), IMPROVED (blue), FIXED (orange)
 * - Unread count badge based on localStorage last-seen date
 * - "View All" expands to show all entries inline
 * - Compact mode for sidebar, full mode for first-time users
 */
const WhatsNew = ({
  entries = [],
  maxEntries = 3,
  compact = false,
  comingSoonText = 'A/B Testing & Smart Scheduling',
  showComingSoon = false,
}) => {
  const [lastSeenDate, setLastSeenDate] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Load last seen date from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setLastSeenDate(parsed.lastSeenDate ? new Date(parsed.lastSeenDate) : null);
      }
    } catch (e) {
      console.error('Error loading WhatsNew state:', e);
    }
  }, []);

  // Mark entries as seen
  const markAsSeen = useCallback(() => {
    try {
      const now = new Date().toISOString();
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ lastSeenDate: now }));
      setLastSeenDate(new Date(now));
    } catch (e) {
      console.error('Error saving WhatsNew state:', e);
    }
  }, []);

  // Check if entry is unread
  const isUnread = useCallback((entryDate) => {
    if (!lastSeenDate) return true;
    return new Date(entryDate) > lastSeenDate;
  }, [lastSeenDate]);

  // Count unread entries
  const unreadCount = entries.filter(entry => isUnread(entry.date)).length;

  // Get displayed entries
  const displayedEntries = isExpanded ? entries : entries.slice(0, maxEntries);

  // Handle expand toggle
  const handleToggleExpand = useCallback(() => {
    setIsExpanded(!isExpanded);
    if (!isExpanded) {
      markAsSeen();
    }
  }, [isExpanded, markAsSeen]);

  // Don't render if no entries
  if (!entries || entries.length === 0) {
    return null;
  }

  // Compact mode for sidebar
  if (compact) {
    return (
      <s-section>
        <s-stack direction="block" gap="small">
          <s-stack direction="inline" justifyContent="space-between" alignItems="center">
            <s-stack direction="inline" gap="small-200" alignItems="center">
              <s-heading>
                What's New
              </s-heading>
              {unreadCount > 0 && (
                <s-badge tone="critical">
                  {String(unreadCount)}
                </s-badge>
              )}
            </s-stack>
            <s-button variant="tertiary" onClick={handleToggleExpand} aria-expanded={isExpanded}>
              {isExpanded ? 'Show Less' : 'View All'}
            </s-button>
          </s-stack>

          <s-stack direction="block" gap="small-200">
            {displayedEntries.map((entry) => (
              <EntryItem key={entry.id} entry={entry} isUnread={isUnread(entry.date)} />
            ))}
          </s-stack>

          {/* Coming Soon Teaser */}
          {showComingSoon && comingSoonText && (
            <s-box padding="small-200" background="subdued" borderRadius="small">
              <s-stack direction="block" gap="small-300">
                <s-stack direction="inline" gap="small-200" alignItems="center">
                  <s-badge tone="info">
                    COMING SOON
                  </s-badge>
                </s-stack>
                <s-paragraph color="subdued">
                  {comingSoonText}
                </s-paragraph>
              </s-stack>
            </s-box>
          )}

        </s-stack>
      </s-section>
    );
  }

  // Full mode
  return (
    <s-section>
      <s-stack direction="block" gap="base">
        <s-stack direction="inline" justifyContent="space-between" alignItems="center">
          <s-stack direction="inline" gap="small-200" alignItems="center">
            <s-heading>
              What's New
            </s-heading>
            {unreadCount > 0 && (
              <s-badge tone="critical">
                {`${unreadCount} new`}
              </s-badge>
            )}
          </s-stack>
          <s-button variant="tertiary" onClick={handleToggleExpand} aria-expanded={isExpanded}>
            {isExpanded ? 'Show Less' : 'View All'}
          </s-button>
        </s-stack>

        <div style={{ maxHeight: isExpanded ? '400px' : 'auto', overflowY: 'auto' }}>
          <s-stack direction="block" gap="small-200">
            {displayedEntries.map((entry) => (
              <EntryItem key={entry.id} entry={entry} isUnread={isUnread(entry.date)} />
            ))}
          </s-stack>
        </div>

      </s-stack>
    </s-section>
  );
};

export default WhatsNew;
